import Tooltip from '@mui/material/Tooltip'

import type { HeaderLabel } from '../CopCaseDetails.types'
import { formatLondonTooltip } from '../utils/londonTooltip'

type CaseTracking = {
  dateTimeClosed?: string | null
}

export type CaseHeaderSummaryProps = {
  title?: string

  layout?: string

  labels?: HeaderLabel[]

  apiResponse?: Record<string, unknown> | null
}

type CaseObject = {
  id?: string | null
  customerReference?: string | null
  dateTimeOpened?: string | null
  number?: string | null
  product?: string | null
  severity?: string | null
  status?: string | null
  subject?: string | null
  projectNumber?: string | null
  serviceAgreement?: string | null
  contactEmail?: string | null
}

type SeverityColor = 'green' | 'yellow' | 'red'

type EntryValue = { value: string; severity?: SeverityColor; tooltip?: string }

const VALUE_BY_DATA_KEY: Record<string, EntryValue> = {
  status: { value: 'Open' },
  number: { value: '20241205-6377777' },
  customerReference: { value: '—' },
  dateTimeOpened: { value: '2024-12-05 09:42' },
  product: { value: 'Transformer Monitoring' },
  severity: { value: 'Low', severity: 'yellow' },
}

const DEFAULT_LABELS: HeaderLabel[] = [
  { labelText: 'Status', dataKey: 'status' },
  { labelText: 'Case Number', dataKey: 'number' },
  { labelText: 'Customer Reference', dataKey: 'customerReference' },
  { labelText: 'Date / Time Opened', dataKey: 'dateTimeOpened' },
  { labelText: 'Product', dataKey: 'product' },
  { labelText: 'Severity', dataKey: 'severity' },
]

const PLACEHOLDER = '—'

const formatDateTime = (iso?: string | null): string => {
  if (!iso) {
    return PLACEHOLDER
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return PLACEHOLDER
  }
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(',', '')
}

const severityToColor = (severity: string): SeverityColor | undefined => {
  switch (severity.toLowerCase()) {
    case 'normal':
      return 'green'
    case 'low':
      return 'yellow'
    case 'medium':
      return 'yellow'
    case 'high':
      return 'red'
    default:
      return undefined
  }
}

const buildValuesFromCase = (
  c: CaseObject,
  isClosed: boolean,
  dateTimeClosed?: string | null,
): Record<string, EntryValue> => {
  const severityRaw = (c.severity || '').trim()
  const dateRaw = isClosed ? dateTimeClosed : c.dateTimeOpened
  const dateEntry: EntryValue = { value: formatDateTime(dateRaw), tooltip: formatLondonTooltip(dateRaw) }

  return {
    status: { value: c.status || PLACEHOLDER },
    number: { value: c.number || PLACEHOLDER },
    customerReference: { value: c.customerReference || PLACEHOLDER },
    dateTimeOpened: dateEntry,
    dateTime: dateEntry,
    product: { value: c.product || PLACEHOLDER },
    severity: {
      value: severityRaw || PLACEHOLDER,
      severity: severityToColor(severityRaw),
    },
  }
}

const DATE_DATA_KEYS = new Set(['dateTime', 'dateTimeOpened'])
const CLOSED_DATE_LABEL = 'Date / Time Closed'

const DateTooltip = ({ value, tooltip }: { value: string; tooltip?: string }) => {
  if (!tooltip) return <>{value}</>

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#1a202c',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 1px 6px rgb(0 0 0 / 30%)',
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
        arrow: { sx: { color: '#fff' } },
      }}
    >
      <span className="cop-datetime-cell">{value}</span>
    </Tooltip>
  )
}

export default function CaseHeaderSummary({ title, layout, labels, apiResponse }: Readonly<CaseHeaderSummaryProps>) {
  const caseObj = (apiResponse?.case as CaseObject | undefined) ?? undefined
  const caseTracking = (apiResponse?.caseTracking as CaseTracking | undefined) ?? undefined
  const isClosed = (caseObj?.status ?? '').toLowerCase() === 'closed'
  const valueByDataKey = caseObj
    ? buildValuesFromCase(caseObj, isClosed, caseTracking?.dateTimeClosed)
    : VALUE_BY_DATA_KEY

  const baseTitle = title || 'Case Details'
  const headerTitle = caseObj?.number ? `${baseTitle}: ${caseObj.number}` : baseTitle
  const resolvedLabels = labels && labels.length > 0 ? labels : DEFAULT_LABELS
  const layoutClass = layout ? ` cop-case-details__summary--${layout}` : ''

  return (
    <div className="cop-case-details__header-summary">
      {

}

      <h1 className="cop-case-details__title">{headerTitle}</h1>

      <div className={`cop-case-details__summary${layoutClass}`}>
        {resolvedLabels.map(({ labelText, dataKey }) => {
          const entry = valueByDataKey[dataKey] ?? { value: PLACEHOLDER }
          const displayLabel = isClosed && DATE_DATA_KEYS.has(dataKey) ? CLOSED_DATE_LABEL : labelText
          return (
            <div key={`${dataKey}-${labelText}`} className="cop-case-details__summary-cell">
              <div className="cop-case-details__summary-label">{displayLabel}</div>
              <div className="cop-case-details__summary-value">
                {entry.severity && (
                  <span
                    className={`cop-case-details__dot cop-case-details__dot--${entry.severity}`}
                  />
                )}
                {entry.tooltip ? (
                  <DateTooltip value={entry.value} tooltip={entry.tooltip} />
                ) : (
                  entry.value
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
